import { Grid2, Paper, useMediaQuery } from "@mui/material";
import { type CSSProperties, type ReactNode, useMemo } from "react";
import {
	Group,
	type Layout,
	Panel,
	useDefaultLayout,
	useGroupRef,
} from "react-resizable-panels";

import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import {
	type UseInspectionFormOptions,
	useInspectionForm,
} from "../hooks/useInspectionForm";
import { InspectionForm } from "./InspectionForm";

const MIN_FORM_PANEL_SIZE = "35%";
const MIN_DETAILS_PANEL_SIZE = "50%";

interface InspectionFormSplitLayoutProps {
	formOptions: UseInspectionFormOptions;
	formLayout?: "split" | "stacked";
	formHeader?: ReactNode;
	// Built by the parent so typing in the form does not re-render it.
	detailsPanel: ReactNode;
	layoutId: string;
	formPanelId: string;
	detailsPanelId: string;
	groupStyle?: CSSProperties;
}

// Owns the form hook so each keystroke re-renders only this layout, not the
// parent page (rolling grid, queries). The hook sits above the desktop/mobile
// branch so switching breakpoints keeps the edits.
export const InspectionFormSplitLayout = ({
	formOptions,
	formLayout,
	formHeader,
	detailsPanel,
	layoutId,
	formPanelId,
	detailsPanelId,
	groupStyle,
}: InspectionFormSplitLayoutProps) => {
	const inspectionForm = useInspectionForm(formOptions);
	const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
	const groupRef = useGroupRef();
	const panelIds = useMemo(
		() => [formPanelId, detailsPanelId],
		[formPanelId, detailsPanelId],
	);
	const { defaultLayout, onLayoutChanged } = useDefaultLayout({
		id: layoutId,
		panelIds,
	});
	const defaultDesktopLayout = useMemo<Layout>(
		() => ({ [formPanelId]: 35, [detailsPanelId]: 65 }),
		[formPanelId, detailsPanelId],
	);

	const formPanel = (
		<Paper elevation={2} sx={{ p: 2, height: "100%", overflow: "auto" }}>
			{formHeader}
			<InspectionForm inspectionForm={inspectionForm} layout={formLayout} />
		</Paper>
	);

	if (!isDesktop)
		return (
			<Grid2 container spacing={1}>
				<Grid2 size={12}>{formPanel}</Grid2>
				<Grid2 size={12}>{detailsPanel}</Grid2>
			</Grid2>
		);

	return (
		<Group
			id={layoutId}
			groupRef={groupRef}
			orientation="horizontal"
			defaultLayout={defaultLayout ?? defaultDesktopLayout}
			onLayoutChanged={onLayoutChanged}
			style={groupStyle}
		>
			<Panel id={formPanelId} minSize={MIN_FORM_PANEL_SIZE}>
				{formPanel}
			</Panel>
			<CustomSeparator
				onDoubleClick={() => groupRef.current?.setLayout(defaultDesktopLayout)}
			/>
			<Panel id={detailsPanelId} minSize={MIN_DETAILS_PANEL_SIZE}>
				{detailsPanel}
			</Panel>
		</Group>
	);
};
